import { ACCOUNT_NOTIFICATION_SETTINGS_PATHS } from '@/shared/constants';
import {
    AlertCatalog,
    AlertDeliveryDestinationProvider,
    AlertDeliveryDestinationsResponse,
    AlertInput,
    AlertsPage,
    AlertTestResult,
    WorkspaceAlert,
} from '@/types';

import { apiClient } from '../http';

interface SlackChannelsResponse {
    channels: Array<{ id: string; isMember: boolean; isPrivate: boolean; name: string }>;
    connected: boolean;
    connectedAccountId?: string;
}

export interface AlertListOptions {
    limit?: number;
    page?: number;
    search?: string;
}

const deliveryDestinationLoaders: Record<
    AlertDeliveryDestinationProvider,
    () => Promise<AlertDeliveryDestinationsResponse>
> = {
    slack: async () => {
        const response = await apiClient.get<SlackChannelsResponse>('/api/notifications/slack/channels');

        return {
            connected: response.connected,
            connectedAccountId: response.connectedAccountId,
            destinations: response.channels.map((channel) => ({
                id: channel.id,
                label: channel.name,
                selectable: channel.isMember,
                visibility: channel.isPrivate ? 'private' : 'public',
            })),
            provider: 'slack',
        };
    },
};

const deliveryProviderConnectionLoaders: Record<AlertDeliveryDestinationProvider, () => Promise<{ url: string }>> = {
    slack: () =>
        apiClient.get<{ url: string }>('/api/auth/account/slack/url', {
            params: { returnTo: ACCOUNT_NOTIFICATION_SETTINGS_PATHS.alerts },
        }),
};

export const alertsService = {
    catalog: () => apiClient.get<AlertCatalog>('/api/alerts/catalog'),
    connectDeliveryProvider: (provider: AlertDeliveryDestinationProvider) =>
        deliveryProviderConnectionLoaders[provider](),
    create: (input: AlertInput) => apiClient.post<WorkspaceAlert, AlertInput>('/api/alerts', input),
    delete: (alertId: string, revision: number) =>
        apiClient.delete<void>(`/api/alerts/${alertId}`, { params: { revision } }),
    deliveryDestinations: (provider: AlertDeliveryDestinationProvider) => deliveryDestinationLoaders[provider](),
    get: (alertId: string) => apiClient.get<WorkspaceAlert>(`/api/alerts/${alertId}`),
    list: ({ limit = 100, page = 1, search }: AlertListOptions = {}) =>
        apiClient.get<AlertsPage>('/api/alerts', { params: { limit, page, search: search || undefined } }),
    test: ({ alert, sourceAlertId }: { alert: AlertInput; sourceAlertId?: string }) =>
        apiClient.post<AlertTestResult, { alert: AlertInput; sourceAlertId?: string }>('/api/alerts/test', {
            alert,
            sourceAlertId,
        }),
    update: (alertId: string, input: Partial<AlertInput>, revision: number) =>
        apiClient.patch<WorkspaceAlert, Partial<AlertInput> & { revision: number }>(`/api/alerts/${alertId}`, {
            ...input,
            revision,
        }),
};
